import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { COMPANY } from "../constants/site";
import { Seo } from "../components/site/Seo";
import { Reveal, SectionHead } from "../components/site/Shared";
import { Icon } from "../components/site/Icon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  ["general", "General question"],
  ["customer", "I'm a household customer"],
  ["vendor", "I'm a service professional"],
  ["launch-updates", "Keep me posted on the mobile app launch"],
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", interest: "general", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(res.data?.message || "Enquiry received.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not send your enquiry. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Us"
        description="Contact the HomeAligns team — questions about households, service professional registration, plans or the mobile app launch. Email support@homealigns.com."
        path="/contact"
      />
      <section className="container-site max-w-5xl py-20 lg:py-24">
        <SectionHead
          level={1}
          eyebrow="Contact us"
          title="We'd love to hear from you."
          sub="Questions about HomeAligns, your account, plans or joining as a service professional — send us a message or email us directly."
        />
        <div className="grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            {sent ? (
              <div className="card-site p-10 text-center" data-testid="contact-success-panel">
                <span className="icon-tile mx-auto !h-14 !w-14">
                  <Icon name="Check" className="h-6 w-6" />
                </span>
                <h2 className="mt-5 font-display text-2xl font-semibold text-ink">Message received.</h2>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-ink/60">
                  Thank you, {form.name.split(" ")[0]}. Your enquiry has been recorded and the
                  HomeAligns team will reply to <strong>{form.email}</strong>.
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another-btn"
                  onClick={() => { setSent(false); setForm({ name: "", email: "", interest: "general", message: "" }); }}
                  className="btn-secondary mt-8"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="card-site space-y-5 p-8" data-testid="contact-form" noValidate={false}>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-sm font-bold text-ink">Full name</label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={update("name")}
                      className="input-site"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-sm font-bold text-ink">Email</label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className="input-site"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-interest" className="mb-1.5 block text-sm font-bold text-ink">This is about</label>
                  <select
                    id="contact-interest"
                    data-testid="contact-interest-select"
                    value={form.interest}
                    onChange={update("interest")}
                    className="input-site"
                  >
                    {INTERESTS.map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-sm font-bold text-ink">Message</label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    rows={5}
                    maxLength={2000}
                    value={form.message}
                    onChange={update("message")}
                    className="input-site resize-none"
                    placeholder="How can we help?"
                  />
                </div>
                <button type="submit" data-testid="contact-submit-btn" disabled={sending} className="btn-primary w-full disabled:opacity-60">
                  {sending ? "Sending…" : "Send message"} <Icon name="ArrowRight" className="h-4 w-4" />
                </button>
                <p className="text-xs leading-relaxed text-ink/45">
                  We'll only use your details to answer this enquiry. See our{" "}
                  <a href="/privacy-policy" className="font-semibold text-teal-deep underline underline-offset-2">Privacy Policy</a>.
                </p>
              </form>
            )}
          </Reveal>

          <Reveal delay={1} className="lg:col-span-2">
            <div className="space-y-6">
              <div className="card-site p-7">
                <span className="icon-tile"><Icon name="Mail" className="h-5 w-5" /></span>
                <h2 className="mt-4 text-base font-bold text-ink">Email us directly</h2>
                <a href={`mailto:${COMPANY.supportEmail}`} data-testid="contact-email-link" className="mt-2 block text-sm font-bold text-teal-deep underline underline-offset-4 hover:text-teal">
                  {COMPANY.supportEmail}
                </a>
                <p className="mt-2 text-xs leading-relaxed text-ink/50">
                  Privacy requests are answered within 30 days; general enquiries sooner.
                </p>
              </div>
              <div className="card-site p-7">
                <span className="icon-tile"><Icon name="MapPin" className="h-5 w-5" /></span>
                <h2 className="mt-4 text-base font-bold text-ink">Registered office</h2>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">{COMPANY.operator}</p>
                <p className="mt-1 text-xs leading-relaxed text-ink/50">{COMPANY.address}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
